import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await publicApi.getCheckoutStatus(sessionId);
        setPaymentData(response.data);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking status:', error);
        setStatus('error');
      }
    };

    checkStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="checkout-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-8 md:p-12 max-w-md w-full text-center"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-2xl font-bold mb-2">Processing Payment</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2" data-testid="success-title">Payment Successful!</h1>
            <p className="text-muted-foreground mb-4">
              Thank you for your purchase of <span className="text-primary font-semibold">{paymentData?.package_name}</span>
            </p>
            {paymentData?.amount && (
              <p className="text-2xl font-bold text-primary mb-6">
                ${paymentData.amount.toFixed(2)} {paymentData.currency?.toUpperCase()}
              </p>
            )}
            <p className="text-sm text-muted-foreground mb-8">
              We'll be in touch shortly to discuss your project.
            </p>
            <Link to="/">
              <Button className="btn-primary" data-testid="back-home-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Processing</h1>
            <p className="text-muted-foreground mb-6">
              Your payment is being processed. You'll receive a confirmation email shortly.
            </p>
            <Link to="/">
              <Button className="btn-primary" data-testid="back-home-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">Session Expired</h1>
            <p className="text-muted-foreground mb-6">
              This checkout session has expired. Please try again.
            </p>
            <Link to="/#pricing">
              <Button className="btn-primary" data-testid="try-again-btn">
                Try Again
              </Button>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">Something Went Wrong</h1>
            <p className="text-muted-foreground mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <Link to="/">
              <Button className="btn-primary" data-testid="back-home-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default CheckoutSuccess;
